import { useNavigate } from "react-router-dom";
import { MESES_OPTIONS } from "../../../enums/meses";
import { useCallback, useEffect, useState } from "react";
import { ISelectOptions } from "../../../types/inputs";
import { Loja } from "../../../services/Lojas";
import { toast } from "react-toastify";
import { IMetaForm } from "../../../types/meta";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

type FormValues = {
  ano: number;
  mes: number;
  uuidMunicipio: string;
  quantidadeTotal: number;
  tipo: "VISTORIAS" | "VISTORIADOR";
};

export const useGoalsFormTemplate = () => {
  const navigate = useNavigate();
  const [lojasOptions, setLojasOptions] = useState<ISelectOptions[]>([]);
  const currentYear = new Date().getFullYear();
  const endYear = 2040;

  const ANOS_OPTIONS = Array.from(
    { length: endYear - currentYear + 1 },
    (_, i) => {
      const year = currentYear + i;
      return { label: String(year), value: year };
    }
  );

  const schema = z.object({
    ano: z.number().int(),
    mes: z.number().int(),
    uuidMunicipio: z.string().nonempty("Selecione a unidade"),
    quantidadeTotal: z
      .number({ invalid_type_error: "Quantidade deve ser um número" })
      .int()
      .min(1, "O valor da quantidade deve ser maior do que zero"),
    tipo: z.enum(["VISTORIAS", "VISTORIADOR"]),
  });

  const {
    control,
    setValue,
    handleSubmit: rhfHandleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { tipo: "VISTORIAS" },
  });

  const handleSubmitWith = (
    userCallback: (data: IMetaForm) => Promise<any> | any
  ) =>
    rhfHandleSubmit(async (values: FormValues) => {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;

      if (
        values.ano < currentYear ||
        (values.ano === currentYear && values.mes < currentMonth)
      ) {
        setError("mes", {
          type: "manual",
          message:
            "Não é permitido cadastrar/editar metas para competências retroativas",
        });
        return;
      }

      const mesString = `${String(values.ano)}-${String(values.mes).padStart(
        2,
        "0"
      )}-01`;

      const PAYLOAD: IMetaForm = {
        mes: mesString,
        quantidadeTotal: values.quantidadeTotal,
        tipo: values.tipo,
        uuidMunicipio: values.uuidMunicipio,
      };

      await userCallback(PAYLOAD);
    });

  const getLojas = useCallback(() => {
    Loja.get()
      .then(({ data }) => {
        const options = data.content.map((loja) => ({
          label: loja.nome,
          value: loja?.endereco?.uuidMunicipio,
        }));
        setLojasOptions(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => {
          toast.error(mensagem);
        }
      );
  }, []);

  useEffect(() => {
    getLojas();
  }, []);

  return {
    navigate,
    ANOS_OPTIONS,
    MESES_OPTIONS,
    lojasOptions,
    control,
    handleSubmitWith,
    errors,
    isSubmitting,
    setValue,
  };
};
